import { useDraggable } from "@dnd-kit/core";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { debounce, isEqual } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import { useImmer } from "use-immer";
import { useAutoSave } from "../../hooks/useAutoSave";
import { AI_VISIBILITY } from "../../../../src/shared/enums.js";
import { extractTextFromTipTap } from "../../../../src/shared/schemas/CharacterSchema.js";
import { CustomFieldSchema, generateId, MAX_NUM_CUSTOM_FIELDS, WorldbuildingEntrySchema } from "../../../../src/shared/schemas/WorldbuildingEntrySchema";
import {
  buildWorldbuildingSavePayload,
  getWorldbuildingOversizedFieldKeys,
  mergeWorldbuildingPreservingOversizedFields,
  storyBibleFieldsNotSavedToastMessage,
  worldbuildingEntryHasOversizedFields,
} from "../../../../src/shared/schemas/storyBibleFieldLimit.js";
import Auth from "../../Auth.js";
import Project from "../../models/Project.js";
import { useAppStore } from "../../stores/appStore.js";
import { useProjectStore } from "../../stores/projectStore.js";
import { themeVariables } from "../../themeVariables.js";
import CustomField from "../CustomField/index.js";
import IconButton from "../IconButton/index.jsx";
import HistoryModal from "../HistoryModal/HistoryModal";
import HistorySelectedItem from "../HistoryModal/HistorySelectedItem";
import useElementHistoryController from "../ElementHistory/useElementHistoryController";
import AnimatedWrapper from "../AnimatedWrapper/index.js";
import ElementVisibilityController from "../ElementVisibilityController";
import StyledSelect from "../StyledSelect/index.js";
import { ReactSelectFocusManagerContext } from "../StyledSelect/ReactSelectFocusManager";
import { RestorableTiptapField } from "../TiptapField";
import { useToast } from "../ToastWrapper/index.js";
import { highlightEntityField } from "src/utils/diffToTiptap";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import EntryMenu from "./EntryMenu/index.js";
import { hasUnsavedEditsSince, reconcileEntityContent, PARENT_SYNCED_METADATA_FIELDS } from "../StoryBible/entitySync";
import { WorldbuildingEntryContainer } from "./styles";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../AppEventPublisher";
import { EXPAND_STORY_BIBLE_ENTITY_EVENT, getStoryBibleEntityExpandRequestId } from "../PersistentChat/mentions/navigateToEntityMention";
import SeriesTag from "../StoryBible/SeriesTag.tsx";
import { worldbuildingEntryToPlainText } from "../StoryBible/Utils";
import { SudoButton } from "../../sudokit/components/SudoButton/index.jsx";
import { cn } from "src/lib/cn.js";
import { useSudowriteStore } from "../../stores/sudowriteStore.js";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { useCrossAppSignalStore, getWorldbuildingTraitCTASignal } from "src/stores/crossAppSignalStore";
import { cloneWorldbuildingFields, formatWorldbuildingTypeLabel } from "../Worldbuilding/useWorldbuildingTemplates";
import { mergeCustomFieldsWithTemplateOrder } from "src/utils/mergeCustomFieldsWithTemplateOrder.js";
import EntityImageGalleryPreview, { MAX_VISIBLE_ALTERNATES } from "../EntityImage/EntityImageGalleryPreview.tsx";
import EntityImageRowThumbnail from "../EntityImage/EntityImageRowThumbnail.tsx";
import { useEntityImageCard, useEntityImageDialogState } from "../EntityImage/lib/useEntityImageCard";
import WorldbuildingImageDialog from "./WorldbuildingImage/WorldbuildingImageDialog.tsx";
import { worldbuildingImageApi } from "./WorldbuildingImage/lib/api";
import { useWorldbuildingImages } from "./WorldbuildingImage/lib/useWorldbuildingImages";
import { useWorldbuildingImageGeneration } from "./WorldbuildingImage/lib/useWorldbuildingImageGeneration";

const SMALL_WIDTH_BREAKPOINT = 600;
const COLLAPSE_ICONS_BREAKPOINT = 600;
const COLLAPSE_ICONS_WHEN_SERIES_BREAKPOINT = 800; // break sooner if series is present
const MOVE_ROLE_SELECTOR_BREAKPOINT = 700;

const WorldbuildingEntry = ({
  draggingId,
  entry,
  handleDeleteEntries,
  selectedItems,
  handleSaveEntry,
  loading,
  toggleEntryCollapsed,
  roleOptions = [],
  readOnly,
  restoreSnapshot,
  isRestoring,
  regenerateTrait,
  isSelected,
  handleSelect,
  projectTitle,
}) => {
  const userId = useAppStore((state) => state.user?.uid);
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const imageDialog = useEntityImageDialogState();
  const images = useWorldbuildingImages(entry.id, { enabled: !entry.collapsed || imageDialog.isOpen });
  const emitLayoutEvent = useSudoLayoutEventEmitter();
  const globalCtaActive = usePersistentChatStore((state) => {
    const cta = state.ctaAction;
    return cta && cta.status === "in_progress";
  });
  const traitSignals = useCrossAppSignalStore((state) => state.signals);
  const worldbuildingDiffs = useStoryBibleStore((state) => state.worldbuildingDiffs);
  const clearWorldbuildingDiff = useStoryBibleStore((state) => state.clearWorldbuildingDiff);
  const clearWorldbuildingFieldDiff = useStoryBibleStore((state) => state.clearWorldbuildingFieldDiff);
  const pendingNewWorldbuildingIds = useStoryBibleStore((state) => state.pendingNewWorldbuildingIds) || [];
  const removePendingNewWorldbuilding = useStoryBibleStore((state) => state.removePendingNewWorldbuilding);
  const upsertWorldbuildingEntity = useStoryBibleStore((state) => state.upsertWorldbuildingEntity);
  const { focusedElement, focusElement } = useContext(ReactSelectFocusManagerContext);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({ id: entry.id });
  const [localEntry, setLocalEntry] = useImmer(entry);
  // Latest committed localEntry, so async save callbacks can detect edits made
  // while their request was in flight.
  const localEntryRef = useRef(localEntry);
  useEffect(() => {
    localEntryRef.current = localEntry;
  }, [localEntry]);
  const persistedEntryRef = useRef(entry);
  const previousParentRef = useRef(entry);
  const scheduledEntityRef = useRef(localEntry);
  const reconcileQueueRef = useRef(() => {});

  const [error, setError] = useState({});
  const isDragging = draggingId === entry.id;
  const [smallWidth, setSmallWidth] = useState(false);
  const { setToast } = useToast();
  const lastVisibilitySetting = useRef(localEntry.aiVisibility);

  // this is used to unset z-index across many fields when it's appropriate.
  // this helps manipulate the various stacking contexts to prevent
  // overlapping in the UX.
  const [isFlat, setIsFlat] = useState(false);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const [entryProjectTitle, setEntryProjectTitle] = useState("");
  const selectRef = useRef(null);
  const [showHoverIcons, setShowHoverIcons] = useState(false);
  const [collapseIcons, setCollapseIcons] = useState(false);
  const [moveRoleSelector, setMoveRoleSelector] = useState(false);
  const [localDirty, setLocalDirty] = useState(false);
  const isTogglingCollapsed = useRef(false);
  const targetCollapsedState = useRef(null);

  const entryDiff = readOnly ? null : worldbuildingDiffs[entry.id];
  const isPendingNew = readOnly ? false : pendingNewWorldbuildingIds.includes(entry.id);
  const hasDiff = isPendingNew || (entryDiff && entryDiff.oldFields && entryDiff.newFields);

  const getFieldDiffValue = useCallback((fieldKey, currentValue) => (
    highlightEntityField(currentValue, fieldKey, entryDiff, isPendingNew)
  ), [isPendingNew, entryDiff]);

  const clearAllWorldbuildingHighlighting = useCallback(() => {
    clearWorldbuildingDiff(entry.id);
    removePendingNewWorldbuilding(entry.id);
  }, [entry.id, clearWorldbuildingDiff, removePendingNewWorldbuilding]);

  const clearFieldHighlighting = useCallback((fieldKey) => {
    // For NEW entities, clicking any field clears all highlights
    if (isPendingNew) {
      clearWorldbuildingDiff(entry.id);
      removePendingNewWorldbuilding(entry.id);
      return;
    }
    // For MODIFIED entities, only clear that specific field
    if (entryDiff) {
      clearWorldbuildingFieldDiff(entry.id, fieldKey);
    }
  }, [entry.id, isPendingNew, entryDiff, clearWorldbuildingDiff, clearWorldbuildingFieldDiff, removePendingNewWorldbuilding]);

  const wbTraitTemplateOrderKey = useMemo(() => {
    const matchingRole = roleOptions.find((role) => role.value === localEntry.role);
    const raw = matchingRole?.template?.defaultFields;
    return raw?.length ? cloneWorldbuildingFields(raw).map((f) => f.label).join("\u0001") : "";
  }, [roleOptions, localEntry.role]);

  const wbCardTraitLabelsKey = useMemo(
    () => (localEntry.customFields?.length ? localEntry.customFields.map((f) => f.label).join("\u0001") : ""),
    [localEntry.customFields],
  );
  const entryNameText = useMemo(() => extractTextFromTipTap(localEntry.name), [localEntry.name]);
  const entryDisplayName = entryNameText || "Element";
  const oversizedFieldKeys = useMemo(() => getWorldbuildingOversizedFieldKeys(localEntry), [localEntry]);

  const handleRegenerateTrait = useCallback(
    (traitId, traitLabel, guidance, isEmpty) => regenerateTrait(entry.id, traitId, traitLabel, guidance, isEmpty),
    [regenerateTrait, entry.id],
  );

  const handleTraitCtaTrigger = useCallback(
    (traitId, traitLabel, isEmpty) => {
      if (globalCtaActive) return;
      emitLayoutEvent("trigger_cta_chat", {
        type: isEmpty ? "generate_worldbuilding_trait" : "rewrite_worldbuilding_trait",
        targetId: entry.id,
        targetName: entryDisplayName,
        displayTitle: isEmpty ? "Generate" : "Rewrite",
        userMessage: `${isEmpty ? "Generate" : "Rewrite"} ${traitLabel} for ${entryDisplayName}`,
        cannedResponse: "Ok, let's do it!",
        entryId: entry.id,
        traitId,
        traitLabel,
      });
    },
    [globalCtaActive, emitLayoutEvent, entry.id, entryDisplayName],
  );

  const revertUnsavedVisibility = useCallback((attemptedVisibility) => {
    const persistedVisibility = persistedEntryRef.current.aiVisibility || AI_VISIBILITY.AUTO;
    if (attemptedVisibility === persistedVisibility || localEntryRef.current.aiVisibility !== attemptedVisibility) {
      return;
    }
    lastVisibilitySetting.current = persistedVisibility;
    setLocalEntry((draft) => {
      draft.aiVisibility = persistedVisibility;
    });
  }, [setLocalEntry]);

  const acceptSavedEntity = useCallback((server, submitted, publish = true) => {
    if (!server || server.id !== submitted.id || !server.name || !Array.isArray(server.customFields) || !server.updatedAt || !Number.isFinite(new Date(server.updatedAt).getTime())) {
      reconcileQueueRef.current(persistedEntryRef.current.updatedAt);
      return;
    }
    if (new Date(server.updatedAt).getTime() < new Date(persistedEntryRef.current.updatedAt).getTime()) return;
    const latest = localEntryRef.current;
    const content = reconcileEntityContent(submitted, latest, server);
    const oversizedKeys = getWorldbuildingOversizedFieldKeys(latest);
    const localFields = new Map((latest.customFields || []).map((field) => [field.id, field]));
    const retainedFields = content.customFields.map((field) => oversizedKeys.has(`customField:${field.id}`)
      ? { ...field, value: localFields.get(field.id).value }
      : field);
    const recovered = mergeWorldbuildingPreservingOversizedFields(content, { ...latest, customFields: retainedFields });
    // Visibility is metadata, but a toggle made after submission is still an intentional save.
    if (latest.aiVisibility !== submitted.aiVisibility) recovered.aiVisibility = latest.aiVisibility;
    if (latest.collapsed !== submitted.collapsed) recovered.collapsed = latest.collapsed;
    // Text saves never write image_id/image_url, so their responses can carry a stale snapshot
    // racing a cover change on a separate endpoint. Always trust local state instead.
    recovered.imageId = latest.imageId;
    recovered.imageUrl = latest.imageUrl;
    const comparableServer = { ...server, imageId: recovered.imageId, imageUrl: recovered.imageUrl };
    persistedEntryRef.current = server;
    const payload = buildWorldbuildingSavePayload(recovered, server).entry;
    const needsSave = hasUnsavedEditsSince(payload, comparableServer) || payload.aiVisibility !== server.aiVisibility;
    localEntryRef.current = recovered;
    scheduledEntityRef.current = recovered;
    setLocalEntry(recovered);
    setLocalDirty(needsSave || worldbuildingEntryHasOversizedFields(recovered));
    // Register replacement work before releasing the old request's promise chain.
    reconcileQueueRef.current(server.updatedAt, needsSave ? recovered : undefined);
    if (publish) {
      previousParentRef.current = recovered;
      handleSaveEntry(recovered);
      upsertWorldbuildingEntity(recovered);
    }
  }, [setLocalEntry, handleSaveEntry, upsertWorldbuildingEntity]);

  // Save function for worldbuilding entry
  const saveEntryFn = useCallback(
    async (updatedEntry) => {
      if (readOnly) return;

      try {
        const { entry: entryPayload, skippedFields } = buildWorldbuildingSavePayload(
          updatedEntry,
          persistedEntryRef.current,
        );
        const validated = await WorldbuildingEntrySchema.validate({
          userId: entryPayload.userId,
          projectId: entryPayload.projectId,
          name: entryPayload.name,
          role: entryPayload.role,
          otherNames: entryPayload.otherNames,
          customFields: entryPayload.customFields,
          displayOrder: entryPayload.displayOrder,
          collapsed: entryPayload.collapsed,
          aiVisibility: entryPayload.aiVisibility,
        });

        const result = await Auth.fetchWithIdToken(`/api/worldbuildingEntries/${updatedEntry.id}`, {
          method: "PUT",
          entry: {
            ...updatedEntry,
            name: validated.name,
            role: validated.role,
            otherNames: validated.otherNames,
            customFields: validated.customFields,
            displayOrder: validated.displayOrder,
            collapsed: validated.collapsed,
            aiVisibility: validated.aiVisibility,
          },
          updatedAt: updatedEntry.updatedAt,
          projectId: updatedEntry.projectId,
        });

        acceptSavedEntity(result.entry, updatedEntry);
        if (skippedFields.length > 0) {
          setToast(storyBibleFieldsNotSavedToastMessage());
        }
      } catch (err) {
        if (err?.name === "ValidationError") {
          setError(err);
          return;
        }
        if (err.status === 409) {
          try {
            const data = await err.response.json();
            if (data.entry) acceptSavedEntity(data.entry, updatedEntry);
            else reconcileQueueRef.current(persistedEntryRef.current.updatedAt);
          } catch (_) {
            // A malformed response cannot authorize a retry with a fresh timestamp.
            reconcileQueueRef.current(persistedEntryRef.current.updatedAt);
          }
          return;
        }
        Sentry.captureException(err, { tags: { feature: "worldbuilding_save" } });
        setToast("Could not save entry");
        revertUnsavedVisibility(updatedEntry.aiVisibility);
      }
    },
    [readOnly, acceptSavedEntity, setToast, revertUnsavedVisibility]
  );

  const applyImageCover = useCallback(
    ({ imageId, imageUrl }) => {
      const updated = { ...localEntryRef.current, imageId, imageUrl };
      localEntryRef.current = updated;
      setLocalEntry(updated);
      handleSaveEntry(updated);
      upsertWorldbuildingEntity(updated);
    },
    [setLocalEntry, handleSaveEntry, upsertWorldbuildingEntity],
  );
  const imageAnalytics = useMemo(
    () => ({
      dialogOpened: (props) => void AppEventPublisher.worldbuildingImage.dialogOpened({ worldbuildingEntryId: entry.id, projectId: entry.projectId, ...props }),
      deleted: (props) => void AppEventPublisher.worldbuildingImage.deleted({ worldbuildingEntryId: entry.id, ...props }),
      coverSet: (props) => void AppEventPublisher.worldbuildingImage.coverSet({ worldbuildingEntryId: entry.id, ...props }),
    }),
    [entry.id, entry.projectId],
  );
  const removeImage = useCallback((imageId) => worldbuildingImageApi.remove(entry.id, imageId), [entry.id]);
  const setImageCover = useCallback((imageId) => worldbuildingImageApi.setCover(entry.id, imageId), [entry.id]);
  const imageCard = useEntityImageCard({
    dialog: imageDialog,
    entity: localEntry,
    images,
    removeImage,
    setCoverImage: setImageCover,
    applyCover: applyImageCover,
    analytics: imageAnalytics,
    sentryFeaturePrefix: "worldbuilding_image",
    setToast,
  });
  // Assigned once useAutoSave (declared further down) has run.
  const flushEntrySaveRef = useRef(null);
  const imageGeneration = useWorldbuildingImageGeneration(entry.id, {
    role: localEntry.role,
    flushPendingSave: () => flushEntrySaveRef.current?.(),
    onSaved: imageCard.handleImagesSaved,
    onGenerated: images.refetch,
    onCoverSet: applyImageCover,
  });
  const isImageGenerating = imageGeneration.status !== "idle";

  // Snapshot creation function
  const createEntrySnapshot = useCallback(async () => {
    if (!localEntry?.id || !localDirty) return; // Only create snapshot if there are changes

    try {
      await Auth.fetchWithIdToken(`/api/worldbuildingEntries/${localEntry.id}/snapshot`, {
        method: "POST",
        projectId: localEntry.projectId,
      });
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "worldbuilding_snapshot" } });
    }
  }, [localEntry, localDirty]);

  // Use shared auto-save hook with blur/visibility handling and snapshot creation
  const { handleBlur: handleEntryBlur, debouncedSave: saveEntry, reconcile } = useAutoSave({
    saveFn: saveEntryFn,
    entity: localEntry,
    createSnapshot: createEntrySnapshot,
    readOnly,
    version: entry?.updatedAt,
    versionPolicy: "explicit",
  });
  reconcileQueueRef.current = reconcile;
  flushEntrySaveRef.current = () => saveEntry.flush();

  const onFieldBlur = useCallback(async () => {
    if (!localDirty) return;
    await handleEntryBlur();
  }, [handleEntryBlur, localDirty]);

  const handleFieldChange = useCallback(
    (fieldKey, json) => {
      if (hasDiff) return;
      setLocalEntry((draft) => {
        draft[fieldKey] = json;
      });
    },
    [hasDiff, setLocalEntry],
  );

  const handleFieldFocus = useCallback(
    (fieldKey) => {
      if (hasDiff) {
        clearFieldHighlighting(fieldKey);
      }
    },
    [hasDiff, clearFieldHighlighting],
  );

  const handleNameChange = useCallback((json) => handleFieldChange("name", json), [handleFieldChange]);
  const handleNameFocus = useCallback(() => handleFieldFocus("name"), [handleFieldFocus]);
  const handleOtherNamesChange = useCallback((json) => handleFieldChange("otherNames", json), [handleFieldChange]);
  const handleOtherNamesFocus = useCallback(() => handleFieldFocus("otherNames"), [handleFieldFocus]);

  useEffect(() => {
    // If we're toggling and waiting for a specific collapsed state,
    // only sync from parent if it matches what we expect
    if (isTogglingCollapsed.current && targetCollapsedState.current !== null) {
      if (entry.collapsed !== targetCollapsedState.current) {
        // Ignore this update, we're still waiting for the right state
        return;
      }
      // Got the state we were waiting for; fall through to the dirty-aware sync
      // below so text typed during the toggle round-trip isn't replaced.
      isTogglingCollapsed.current = false;
      targetCollapsedState.current = null;
    }

    const shouldPreserveLocalVisibility = lastVisibilitySetting.current && entry.aiVisibility !== lastVisibilitySetting.current;

    if (new Date(entry.updatedAt).getTime() < new Date(persistedEntryRef.current.updatedAt).getTime()) return;
    const previousParent = previousParentRef.current;
    previousParentRef.current = entry;
    if (entry.updatedAt !== previousParent.updatedAt) {
      // Bulk metadata responses only change metadata in the parent. They do not
      // provide a new content snapshot and must not replace the persisted content.
      const authority = hasUnsavedEditsSince(entry, previousParent)
        ? entry
        : { ...persistedEntryRef.current, ...Object.fromEntries(PARENT_SYNCED_METADATA_FIELDS.map((key) => [key, entry[key]])) };
      acceptSavedEntity(authority, persistedEntryRef.current, false);
      return;
    }

    if (localDirty) {
      // Only sync metadata fields (PARENT_SYNCED_METADATA_FIELDS in StoryBible/entitySync.ts),
      // preserve content edits in progress
      setLocalEntry((draft) => {
        draft.updatedAt = entry.updatedAt;
        draft.collapsed = entry.collapsed;
        draft.displayOrder = entry.displayOrder;
        if (!shouldPreserveLocalVisibility) {
          draft.aiVisibility = entry.aiVisibility;
        }
      });
    } else if (shouldPreserveLocalVisibility) {
      // this stops the flickering back to old visibility when the user changes the
      // visibility and before changes are saved
      setLocalEntry((draft) => {
        Object.assign(draft, entry);
        draft.aiVisibility = lastVisibilitySetting.current;
      });
    } else {
      setLocalEntry(entry);
    }
  }, [entry, setLocalEntry, localDirty, acceptSavedEntity]);

  // Run after parent sync so template order isn't overwritten by stale `entry` on the same tick.
  // Do not drop blank non-template traits here: users add traits, name them, then fill the body;
  // dropping only runs on role/type change (handleRoleChange) where stale template rows are cleared.
  useEffect(() => {
    if (readOnly || !wbTraitTemplateOrderKey) return;
    const matchingRole = roleOptions.find((role) => role.value === localEntry.role);
    const templateFields = matchingRole?.template?.defaultFields?.length
      ? cloneWorldbuildingFields(matchingRole.template.defaultFields)
      : null;
    if (!templateFields?.length) return;
    setLocalEntry((draft) => {
      const merged = mergeCustomFieldsWithTemplateOrder(
        draft.customFields,
        templateFields,
        (tf) => cloneWorldbuildingFields([tf])[0],
        { dropBlankExtrasNotInTemplate: false, insertMissingTemplateFields: false },
      );
      if (isEqual(merged, draft.customFields)) return;
      draft.customFields = merged;
    });
  }, [readOnly, roleOptions, localEntry.role, setLocalEntry, wbCardTraitLabelsKey, wbTraitTemplateOrderKey]);

  const debouncedValidateForErrors = useMemo(
    () =>
      debounce((entryToValidate) => {
        WorldbuildingEntrySchema.validate(entryToValidate)
          .then(() => setError({}))
          .catch((e) => {
            setError(e);
          });
      }, 400),
    [],
  );

  useEffect(() => {
    debouncedValidateForErrors(localEntry);
  }, [debouncedValidateForErrors, localEntry]);

  useEffect(() => () => debouncedValidateForErrors.cancel(), [debouncedValidateForErrors]);

  useEffect(() => {
    if (readOnly || isTogglingCollapsed.current) return;
    if (localEntry !== localEntryRef.current || isEqual(localEntry, scheduledEntityRef.current)) return;
    scheduledEntityRef.current = localEntry;
    if (!isEqual(localEntry, entry)) {
      setLocalDirty(true);
      saveEntry(localEntry);
    } else {
      // Converged with the parent copy — nothing unsaved remains, so future parent
      // updates may fully sync again. Cancel any pending debounced save: because this
      // effect also runs on `entry` changes (before the parent-sync effect's
      // setLocalEntry commits), response reconciliation can schedule a save of the
      // pre-response localEntry, which would send a redundant stale-version PUT.
      setLocalDirty(false);
      saveEntry.cancel();
    }
  }, [entry, localEntry, readOnly, saveEntry]);

  useEffect(() => {
    if (columnWidths.center.current <= SMALL_WIDTH_BREAKPOINT) {
      setSmallWidth(true);
    } else {
      setSmallWidth(false);
    }
    const collapseIconsBreakpoint = entry.seriesId ? COLLAPSE_ICONS_WHEN_SERIES_BREAKPOINT : COLLAPSE_ICONS_BREAKPOINT;
    if (columnWidths.center.current <= collapseIconsBreakpoint) {
      setCollapseIcons(true);
    } else {
      setCollapseIcons(false);
    }
    if (columnWidths.center.current <= MOVE_ROLE_SELECTOR_BREAKPOINT) {
      setMoveRoleSelector(true);
    } else {
      setMoveRoleSelector(false);
    }
  }, [columnWidths.center, entry.seriesId]);

  useEffect(() => {
    const fetchEntryProjectTitle = async () => {
      const entryProject = await Project.get(entry.projectId);
      setEntryProjectTitle(entryProject?.title || "");
    };

    if (projectTitle) {
      setEntryProjectTitle(projectTitle);
    } else {
      fetchEntryProjectTitle();
    }
  }, [entry.projectId, projectTitle]);

  // Wrapper for toggle to set flags before calling parent
  const handleToggleCollapsed = useCallback(() => {
    // Cancel any pending debounced saves to prevent race condition
    if (typeof saveEntry.cancel === "function") {
      saveEntry.cancel();
    }

    const newCollapsedState = !localEntry.collapsed;

    // Set flags to prevent parent updates during toggle
    isTogglingCollapsed.current = true;
    targetCollapsedState.current = newCollapsedState;

    // Optimistically update local UI
    setLocalEntry((draft) => {
      draft.collapsed = newCollapsedState;
    });

    // Pass localEntry so the parent saves with the latest local data
    // (including any unsaved text edits), not its stale copy
    toggleEntryCollapsed(entry.id, localEntry);
  }, [localEntry, entry.id, toggleEntryCollapsed, setLocalEntry, saveEntry]);

  useEffect(() => {
    const handleExpandRequest = (event) => {
      const requestId = getStoryBibleEntityExpandRequestId(event);
      if (requestId !== `worldbuilding::${entry.id}` || !localEntry.collapsed || readOnly) {
        return;
      }

      handleToggleCollapsed();
    };

    window.addEventListener(EXPAND_STORY_BIBLE_ENTITY_EVENT, handleExpandRequest);
    return () => window.removeEventListener(EXPAND_STORY_BIBLE_ENTITY_EVENT, handleExpandRequest);
  }, [entry.id, handleToggleCollapsed, localEntry.collapsed, readOnly]);

  const onDeleteEntry = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (isSelected && selectedItems.length > 1) {
      handleDeleteEntries(selectedItems.map((item) => item.id));
    } else {
      handleDeleteEntries([entry.id]);
    }
  };

  const handleRoleChange = (selectedOption) => {
    const matchingRole = roleOptions.find((role) => role.value === selectedOption.value);
    const fields = matchingRole?.template?.defaultFields?.length
      ? cloneWorldbuildingFields(matchingRole.template.defaultFields)
      : [{ id: generateId(15), label: "Description" }];
    AppEventPublisher.storyBible.worldbuilding.roleChange(selectedOption.value);
    setLocalEntry((draft) => {
      draft.role = selectedOption.value;
      draft.customFields = mergeCustomFieldsWithTemplateOrder(draft.customFields, fields, (tf) => cloneWorldbuildingFields([tf])[0]);
    });

    focusedElement.current = selectRef.current;
    focusElement();
    focusedElement.current = null;
  };

  const handleAiVisibilityChange = (aiVisibility) => {
    if (!aiVisibility) {
      return;
    }

    if (aiVisibility === localEntry.aiVisibility) {
      return;
    }

    AppEventPublisher.storyBible.toggleVisibility(StoryBibleType.worldbuilding);
    lastVisibilitySetting.current = aiVisibility;

    setLocalEntry((draft) => {
      draft.aiVisibility = aiVisibility;
    });
  };

  const handleCopyEntry = async () => {
    try {
      const plainText = worldbuildingEntryToPlainText(localEntry);
      await navigator.clipboard.writeText(plainText);
      setToast("Worldbuilding copied");
      AppEventPublisher.storyBible.worldbuilding.copyWorldbuilding();
    } catch (error) {
      setToast("Oops, copying failed. Try again");
    }
  };

  const [customFieldIdToFocus, setCustomFieldIdToFocus] = useState(null);

  const actuallyCollapsed = entry.collapsed || isDragging;

  const historyController = useElementHistoryController({
    elementId: entry.id,
    sourceTable: "worldbuilding_entries",
    projectId: entry.projectId,
    restore: async (snapshot) => restoreSnapshot(entry.id, snapshot.id),
    listLabel: "Element History",
  });

  const selectedHistoryItem = historyController.selectedSnapshot ? (
    <HistorySelectedItem
      key={historyController.selectedSnapshot.id}
      snapshot={historyController.selectedSnapshot}
      projectId={entry.projectId}
    />
  ) : null;

  const handleAddCustomField = useCallback(async () => {
    const fields = localEntry.customFields ?? [];
    if (fields.length >= MAX_NUM_CUSTOM_FIELDS) {
      setToast(`You can only have ${MAX_NUM_CUSTOM_FIELDS} custom fields per element.`);
      return;
    }

    AppEventPublisher.storyBible.action(StoryBibleType.worldbuilding, StoryBibleAction.AddCustomField);
    const newCustomField = CustomFieldSchema.cast({});
    setCustomFieldIdToFocus(newCustomField.id);
    setLocalEntry((draft) => {
      const next = draft.customFields ?? [];
      draft.customFields = [...next, newCustomField];
    });
  }, [localEntry.customFields, setLocalEntry, setToast]);

  let selectedRole;
  if (localEntry.role === "sudo-custom") {
    selectedRole = null;
  } else {
    selectedRole = roleOptions.find((role) => role.value === localEntry.role);
    if (!selectedRole) {
      // this is custom to this unique entry.
      selectedRole = { value: localEntry.role, label: formatWorldbuildingTypeLabel(localEntry.role) || localEntry.role };
    }
  }
  const dragCursor = readOnly ? "default" : isDragging ? "grabbing" : "grab";
  const motionProps = readOnly
    ? {}
    : {
        //
        initial: { opacity: 0, height: 0 },
        animate: { opacity: 1, height: "auto" },
        exit: { opacity: 0, height: 0 },
        transition: { duration: 0.25, ease: "easeInOut" },
      };

  return (
    <WorldbuildingEntryContainer
      ref={setNodeRef}
      {...attributes}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      $moveRoleSelector={moveRoleSelector}
      onMouseEnter={() => setShowHoverIcons(true)}
      onMouseLeave={() => setShowHoverIcons(false)}
      onFocus={() => setShowHoverIcons(true)}
      onBlur={() => setShowHoverIcons(false)}
    >
      <div
        id={`story-bible-worldbuilding-${entry.id}`}
        className={classNames(
          "form-container",
          "entry-card",
          { "collapsed-entity": entry.collapsed },
          { "expanded-entity": !entry.collapsed },
          { "selected-entity": isSelected, "read-only": readOnly }
        )}
        onClick={(e) => handleSelect(entry, e.shiftKey, e.ctrlKey || e.metaKey, e)}
        data-selectable="worldbuilding"
      >
        <div className={classNames("header", { "small-width": smallWidth || moveRoleSelector })}>
          <div className={`entity-header-left-side-container`}>
            {!readOnly && (
              <div className="grabber-container">
                <InlineSVG
                  ref={setActivatorNodeRef}
                  {...listeners}
                  style={{ opacity: showHoverIcons ? 1 : 0 }}
                  src="/assets/grabber.svg"
                />
              </div>
            )}
            <div className="entity-header-left-side">
              {!readOnly && (
                <div
                  className="chevron-container"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      if (readOnly) return;
                      handleToggleCollapsed();
                    }
                  }}
                >
                  <InlineSVG
                    src="/assets/chevron-down.svg"
                    className={classNames("chevron", { "rotated-chevron": actuallyCollapsed })}
                    alt="disclosure arrow"
                    onClick={() => {
                      if (readOnly) return;
                      handleToggleCollapsed();
                    }}
                    aria-label={`${actuallyCollapsed ? "Expand" : "Collapse"} worldbuilding element details`}
                  />
                </div>
              )}
              <EntityImageRowThumbnail
                visible={entry.collapsed || isDragging}
                containerGap={8}
                imageUrl={localEntry.imageUrl}
                imageFailed={imageCard.coverImageFailed}
                onImageError={imageCard.markCoverImageFailed}
                isGenerating={isImageGenerating}
                readOnly={readOnly}
                onClick={imageCard.handleCoverClick}
                ariaLabel={`${entryDisplayName}'s image${isImageGenerating ? " (generating)" : ""}`}
                tooltipText={readOnly ? "No image yet" : "Add an image"}
                tooltipDisabled={!!localEntry.imageUrl && !imageCard.coverImageFailed}
              />
              <div className="name-and-role">
                <RestorableTiptapField
                  loading={loading}
                  value={getFieldDiffValue("name", localEntry.name) ?? localEntry.name}
                  className="entry-name title-display"
                  placeholder="Name"
                  onChange={handleNameChange}
                  onFocus={handleNameFocus}
                  onBlur={onFieldBlur}
                  readOnly={readOnly}
                  restoring={isRestoring}
                  searchableId={`wb-${entry.id}-name`}
                  searchableName={`Worldbuilding: ${entryNameText || "Name"}`}
                  userId={userId}
                  projectId={localEntry.projectId}
                  fieldLimitExceeded={oversizedFieldKeys.has("name")}
                />
                <StyledSelect
                  disabled={loading}
                  className={cn("role-selector transition-opacity", loading && "opacity-50")}
                  styleOverrides={{
                    backgroundColor: themeVariables.documentBackground,
                    ...(oversizedFieldKeys.has("role")
                      ? { boxShadow: "inset 0 0 0 1px var(--error-red, #d14343)", borderRadius: "6px" }
                      : {}),
                  }}
                  ref={selectRef}
                  value={selectedRole}
                  options={roleOptions}
                  onChange={handleRoleChange}
                  onMenuOpen={() => setIsFlat(true)}
                  onMenuClose={() => setIsFlat(false)}
                  creatable
                  placeholder="Type"
                  isDisabled={readOnly}
                  aria-label={`Select the worldbuilding element type for ${localEntry.name}`}
                  readOnly={readOnly}
                />
                {entry.seriesId && <SeriesTag projectTitle={entryProjectTitle} />}
              </div>
            </div>
          </div>
          {!readOnly && !collapseIcons && (
            <div className="entity-header-right-side">
              <ElementVisibilityController
                onClick={() => {
                  handleAiVisibilityChange(localEntry.aiVisibility === AI_VISIBILITY.HIDDEN ? AI_VISIBILITY.AUTO : AI_VISIBILITY.HIDDEN);
                }}
                isVisible={localEntry.aiVisibility !== AI_VISIBILITY.HIDDEN}
                shouldShow={showHoverIcons}
                faded={false}
              />
              <AnimatedWrapper isVisible={showHoverIcons}>
                <IconButton
                  className="history"
                  onClick={() => historyController.open()}
                  src="/assets/clock.svg"
                  ariaLabel="Open History"
                  hoverTipText="History"
                  iconSize={20}
                />
              </AnimatedWrapper>
              <AnimatedWrapper isVisible={showHoverIcons}>
                <IconButton
                  onClick={handleCopyEntry}
                  src="/assets/duplicate.svg"
                  ariaLabel="Copy Worldbuilding"
                  hoverTipText="Copy"
                  iconSize={20}
                />
              </AnimatedWrapper>
              <EntryMenu
                handleDelete={onDeleteEntry}
                loading={loading}
                disabled={readOnly}
                roleLabel={selectedRole?.label}
                isSelected={isSelected}
                selectedItems={selectedItems}
              />
            </div>
          )}
          {!readOnly && collapseIcons && (
            <EntryMenu
              handleDelete={onDeleteEntry}
              loading={loading}
              disabled={readOnly}
              roleLabel={selectedRole?.label}
              isSelected={isSelected}
              selectedItems={selectedItems}
              otherActions={[
                <ElementVisibilityController
                  key="visibility"
                  onClick={() => handleAiVisibilityChange(localEntry.aiVisibility === AI_VISIBILITY.HIDDEN ? AI_VISIBILITY.AUTO : AI_VISIBILITY.HIDDEN)}
                  isVisible={localEntry.aiVisibility !== AI_VISIBILITY.HIDDEN}
                  shouldShow={true}
                  faded={false}
                  isMenuButton={true}
                />,
                <div
                  key="history"
                  className="menu-item"
                  onClick={() => historyController.open()}
                >
                  <div className="icon">
                    <InlineSVG src="/assets/clock.svg" />
                  </div>
                  <div className="label">History</div>
                </div>,
                <div
                  key="copy"
                  className="menu-item"
                  onClick={handleCopyEntry}
                >
                  <div className="icon">
                    <InlineSVG src="/assets/duplicate.svg" />
                  </div>
                  <div className="label">Copy</div>
                </div>,
              ]}
            />
          )}
        </div>
        {!isDragging && (
          <AnimatePresence>
            {!entry.collapsed && (
              <motion.div
                {...motionProps}
                className="flex flex-col gap-[16px]"
              >
                <div className="field image-field flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className={cn("key transition-opacity", loading && "opacity-50")}>Images</div>
                    {/* The preview grid swaps its inline "+" add tile for a "+N" overflow tile once
                        alternates exceed the visible cap; this header button covers only that case. */}
                    {!readOnly && images.totalAlternateCount > MAX_VISIBLE_ALTERNATES && (
                      <SudoButton
                        variant="tertiary"
                        size="small"
                        leftIcon="/assets/add.svg"
                        onClick={imageCard.handleAddClick}
                        disabled={loading}
                      >
                        Add image
                      </SudoButton>
                    )}
                  </div>
                  <EntityImageGalleryPreview
                    images={images.images}
                    totalAlternateCount={images.totalAlternateCount}
                    isLoading={images.isLoading}
                    coverImageUrl={localEntry.imageUrl}
                    coverImageFailed={imageCard.coverImageFailed}
                    onCoverImageError={imageCard.markCoverImageFailed}
                    isGenerating={isImageGenerating}
                    readOnly={readOnly}
                    onCoverClick={imageCard.handleCoverClick}
                    onAddClick={imageCard.handleAddClick}
                    onAlternateClick={imageCard.handleAlternateClick}
                    onOverflowClick={imageCard.handleOverflowClick}
                    onRemoveImage={imageCard.handleDeleteImage}
                    entityDisplayName={entryDisplayName}
                    imageNoun="image"
                  />
                </div>
                <div className={classNames("tiptap-fields", { flat: isFlat }, "flex flex-col gap-[16px]")}>
                  <div className="small-field-row">
                    <div className="field small-style">
                      <div className={cn("key transition-opacity", loading && "opacity-50")}>Other Names</div>
                      <RestorableTiptapField
                        loading={loading}
                        value={getFieldDiffValue("otherNames", localEntry.otherNames) ?? localEntry.otherNames}
                        className="other-names"
                        placeholder="Other Names"
                        onChange={handleOtherNamesChange}
                        onFocus={handleOtherNamesFocus}
                        onBlur={onFieldBlur}
                        readOnly={readOnly}
                        restoring={isRestoring}
                        searchableId={`wb-${entry.id}-otherNames`}
                        searchableName={`${entryDisplayName}: Other Names`}
                        userId={userId}
                        projectId={localEntry.projectId}
                        fieldLimitExceeded={oversizedFieldKeys.has("otherNames")}
                      />
                    </div>
                  </div>
                  {localEntry &&
                    localEntry.customFields?.length > 0 &&
                    localEntry.customFields.map((field, index) => {
                      const customFieldKey = `custom.${field.label}`;
                      const customFieldDiffValue = getFieldDiffValue(customFieldKey, field.value);
                      return (
                        <CustomField
                          loading={loading}
                          key={field.id || index}
                          field={field}
                          index={index}
                          setEntity={setLocalEntry}
                          readOnly={readOnly}
                          isRestoring={isRestoring}
                          setIsFlat={setIsFlat}
                          isFlat={isFlat}
                          regenerateTrait={handleRegenerateTrait}
                          autoFocus={field.id === customFieldIdToFocus}
                          searchableId={`wb-${entry.id}-${field.id}`}
                          searchableName={`${entryDisplayName}: ${field.label}`}
                          onBlur={onFieldBlur}
                          userId={userId}
                          projectId={localEntry.projectId}
                          diffValue={customFieldDiffValue}
                          diffFieldKey={customFieldKey}
                          onDiffFieldChange={clearFieldHighlighting}
                          onClearDiff={clearFieldHighlighting}
                          ctaEnabled={!!hasAccessToAgenticChat}
                          ctaShimmer={!!traitSignals[getWorldbuildingTraitCTASignal(entry.id, field.id)]}
                          globalCtaActive={!!globalCtaActive}
                          onCtaTrigger={handleTraitCtaTrigger}
                          fieldLimitExceeded={oversizedFieldKeys.has(`customField:${field.id}`)}
                        />
                      );
                    })}
                </div>
                {readOnly ? null : (
                  <div className="entity-footer">
                    <SudoButton
                      variant="tertiary"
                      onClick={(e) => handleAddCustomField()}
                      leftIcon="/assets/add.svg"
                      disabled={readOnly || loading}
                      size="small"
                    >
                      Add Trait
                    </SudoButton>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
      {error.message && <div className="error">Cannot save entry: {error.message}</div>}

      <HistoryModal
        isOpen={historyController.modalProps.isOpen}
        onClose={historyController.modalProps.onClose}
        selectedItem={selectedHistoryItem}
        historyItems={[]}
        onSelectItem={historyController.setSelectedSnapshot}
        onRestoreItem={historyController.modalProps.onRestoreItem}
        titleIcon="/assets/clock.svg"
        title={"Element History"}
        selectedItemId={historyController.selectedSnapshot?.id}
        sourceTable={"worldbuilding_entries"}
        loading={historyController.modalProps.loading}
        restoring={historyController.modalProps.restoring}
        fetchHistory={historyController.fetchHistory}
        usePagination={true}
        isCreatingSnapshot={historyController.modalProps.isCreatingSnapshot}
      />
      <WorldbuildingImageDialog
        {...imageCard.dialogProps}
        {...imageGeneration}
        entryId={entry.id}
        entryName={entryDisplayName}
        entryTypeLabel={localEntry.role ? formatWorldbuildingTypeLabel(localEntry.role) || localEntry.role : null}
        entityImageUrl={imageCard.coverImageFailed ? null : localEntry.imageUrl}
        entityImagePrompt={imageCard.coverImagePrompt}
        imagesLoading={images.isLoading}
        onAddImage={imageCard.handleGenerateFromGallery}
      />
    </WorldbuildingEntryContainer>
  );
};

export default WorldbuildingEntry;
